import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  TableContainer,
  TablePagination,
  Paper,
  Stack,
} from "@mui/material";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import SmsIcon from "@mui/icons-material/Sms";
import SendIcon from "@mui/icons-material/Send";
import PendingActionsIcon from "@mui/icons-material/PendingActions";

function Wallet() {
  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [totalMsg, setTotalMsg] = useState(0);
  const [totalSent, setTotalSent] = useState(0);
  const [remaining, setRemaining] = useState(0);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch total SMS count
        const accountSnap = await getDocs(collection(db, "whatsappAccount"));
        let total = 0;
        accountSnap.forEach((doc) => {
          total += doc.data().totalMsg || 0;
        });
        setTotalMsg(total);

        // Fetch message history
        const historySnap = await getDocs(collection(db, "whatsappHistory"));
        const data = historySnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setMessages(data);
        setFilteredMessages(data);

        const sentCount = data.reduce((sum, m) => sum + (m.sentCount || 0), 0);
        setTotalSent(sentCount);

        // Remaining SMS
        setRemaining(total - sentCount);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    fetchData();
  }, []);

  // Handle search
  useEffect(() => {
    if (search.trim() === "") {
      setFilteredMessages(messages);
    } else {
      const filtered = messages.filter(
        (msg) =>
          msg.content?.toLowerCase().includes(search.toLowerCase()) ||
          (msg.sentCount || 0).toString().includes(search)
      );
      setFilteredMessages(filtered);
      setPage(0);
    }
  }, [search, messages]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={3}>
        WhatsApp Wallet
      </Typography>

      {/* Top Cards */}
      <Grid className="row" spacing={2} mb={4}>
        <Grid className="col-sm-4 mb-3" md={4}>
          <Card
            sx={{
              p: 3,
              boxShadow: 6,
              background: "linear-gradient(135deg, #3b82f6, #2563eb)",
              color: "#fff",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <SmsIcon sx={{ fontSize: 40, mb: 1 }} />
            <Typography>Total SMS</Typography>
            <Typography variant="h6" fontWeight={700}>
              {totalMsg}
            </Typography>
          </Card>
        </Grid>

        <Grid className="col-sm-4 mb-3" md={4}>
          <Card
            sx={{
              p: 3,
              boxShadow: 6,
              background: "linear-gradient(135deg, #10b981, #047857)",
              color: "#fff",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <SendIcon sx={{ fontSize: 40, mb: 1 }} />
            <Typography>Total Sent</Typography>
            <Typography variant="h6" fontWeight={700}>
              {totalSent}
            </Typography>
          </Card>
        </Grid>

        <Grid className="col-sm-4 mb-3" md={4}>
          <Card
            sx={{
              p: 3,
              boxShadow: 6,
              background: "linear-gradient(135deg, #f59e0b, #b45309)",
              color: "#fff",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <PendingActionsIcon sx={{ fontSize: 40, mb: 1 }} />
            <Typography>Remaining SMS</Typography>
            <Typography variant="h6" fontWeight={700}>
              {remaining}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Search Box */}
      <TextField
        label="Search Messages"
        variant="outlined"
        size="small"
        fullWidth
        mb={2}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
      />

      {/* WhatsApp History Table */}
      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table>
          <TableHead sx={{ background: "#f3f4f6" }}>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Sent Count</TableCell>
              <TableCell>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMessages
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((msg, i) => (
                <TableRow key={msg.id}>
                  <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                  <TableCell>{msg.content || "-"}</TableCell>
                  <TableCell>{msg.sentCount || 0}</TableCell>
                  <TableCell>
                    {msg.createdAt?.toDate
                      ? msg.createdAt.toDate().toLocaleString()
                      : "-"}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={filteredMessages.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
}

export default Wallet;